import React, { useState } from "react";
import Navigation from "../components/Navigation";
import { getAccessToken } from "../utils/auth";

const AdminSubscriptionPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  const validateEmail = (e: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const trimmed = email.trim();
    if (!validateEmail(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }

    setBusy(true);
    try {
      const res = await fetch(
        `https://api.adjusterphoto.com/admin/update-user-subscription`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAccessToken() || ""}`,
          },
          credentials: "include",
          body: JSON.stringify({ user_email: trimmed }),
        },
      );

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Request failed with status ${res.status}`);
      }

      setSuccess("Subscription toggled successfully.");
      setEmail("");
    } catch (err: any) {
      setError(err?.message || "Failed to update subscription.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0b0f]">
      <Navigation />
      <div className="flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8 opacity-0 animate-slide-up stagger-1">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-400 to-purple-600 mb-4 shadow-lg shadow-purple-500/20">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-zinc-100">Admin Panel</h1>
            <p className="text-zinc-500 mt-2 text-sm">
              Toggle a user's subscription status
            </p>
          </div>

          {/* Form Card */}
          <div className="card-dark rounded-2xl p-6 shadow-xl opacity-0 animate-slide-up stagger-2">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-zinc-400 mb-2"
                >
                  User Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  placeholder="user@example.com"
                  onChange={(e) => setEmail(e.target.value)}
                  className={`input-dark w-full px-4 py-3 rounded-xl text-zinc-100 ${
                    email && !validateEmail(email)
                      ? "border-red-500/50 focus:border-red-500"
                      : ""
                  }`}
                  required
                />
                {email && !validateEmail(email) && (
                  <p className="mt-2 text-sm text-red-400">
                    Enter a valid email address.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={busy}
                className="btn-primary w-full py-3.5 rounded-xl text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {busy ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Updating...
                  </span>
                ) : (
                  "Toggle Subscription"
                )}
              </button>
            </form>

            {success && (
              <div className="alert-success mt-4">{success}</div>
            )}
            {error && (
              <div className="alert-error mt-4">{error}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSubscriptionPage;
